import { ReactNode, useCallback, useLayoutEffect, useRef } from 'react';
import styled from 'styled-components';
import { isFunction } from '@agilepoint/utils-common';
import { Label } from '../../label';
import { NumericBox, INumericBoxInstance } from '../../numeric-box';
import { StackPanel } from '../../stack-panel';
import { clamp } from '../../../utils';

interface IValueLabelProps {
    isHorizontallyAligned?: boolean;
    inRangeMode?: boolean;
    start: number;
    end: number;
    min: number;
    max: number;
    step: number;
    editable?: boolean;
    disabled?: boolean;
    onEndChange(value: number): void;
    onStartChange(value: number): void;
    onRenderLabel?(value: number): ReactNode;
}

const NumericBoxField = styled(NumericBox).attrs({
    className: String('ap-slider-valueInput'),
    hideSpinner: true
}) <{ $hidden?: boolean }>`
    opacity: ${({ $hidden }) => $hidden ? 0 : 1};

    .ap-numericBox {
        height: 30px;
        width: 50px;
        padding: 0;

        .ap-textbox {
            text-align: center;
        }
    }
`;

export function ValueLabel(props: IValueLabelProps) {
    const {
        isHorizontallyAligned, inRangeMode, end, start, min, max, editable, step,
        disabled, onEndChange, onStartChange, onRenderLabel
    } = props;
    const startValueFieldRef = useRef<INumericBoxInstance>();
    const endValueFieldRef = useRef<INumericBoxInstance>();

    useLayoutEffect(() => {
        startValueFieldRef.current?.setValue(start.toString());
    }, [start]);

    useLayoutEffect(() => {
        endValueFieldRef.current?.setValue(end.toString());
    }, [end]);

    const _onEndChange = useCallback(() => {
        const { current } = endValueFieldRef;
        if (!current) {
            return;
        }
        const newValue = clamp(Number(current.getFieldValue()), inRangeMode ? start + step : start, max);
        onEndChange(newValue);
        endValueFieldRef.current?.setValue(newValue.toString());
    }, [inRangeMode, max, onEndChange, start, step]);

    const _onStartChange = useCallback(() => {
        const { current } = startValueFieldRef;
        if (!current) {
            return;
        }
        const newValue = clamp(Number(current.getFieldValue()), min, inRangeMode ? end - step : end);
        onStartChange(newValue);
        startValueFieldRef.current?.setValue(newValue.toString());
    }, [end, inRangeMode, min, onStartChange, step]);

    const renderNonEditableFields = () => (
        isFunction(onRenderLabel) ? (
            <>
                <Label>{inRangeMode ? onRenderLabel(start) : ''}</Label>
                <Label>{onRenderLabel(end)}</Label>
            </>
        ) : (
            <>
                <Label>{inRangeMode ? start : ''}</Label>
                <Label>{end}</Label>
            </>
        )
    );

    const renderEditableFields = () => (
        <>
            <NumericBoxField key="start-value" defaultValue={start} $hidden={!inRangeMode} componentRef={startValueFieldRef}
                min={min} max={end - step} step={step} disabled={disabled} onBlur={_onStartChange} />
            <NumericBoxField key="end-value" defaultValue={end} componentRef={endValueFieldRef}
                min={start + step} max={max} step={step} disabled={disabled} onBlur={_onEndChange} />
        </>
    );

    return (
        <StackPanel justifyContent="space-between" orientation={isHorizontallyAligned ? 'horizontal' : 'vertical'}
            flip={!isHorizontallyAligned} inline={false}>
            {editable && !isFunction(onRenderLabel) ? renderEditableFields() : renderNonEditableFields()}
        </StackPanel>
    );
}